/**
 * /catalog — the course catalog (M27).
 *
 * What completing a section is worth and for how long: PHTLS 16 h good for
 * 4 years, ACLS 8 h for 2, DAA 8 h initial / 4 h recert for 2 with a QRV
 * every 6 months, monthly CE that never expires. Sections link to a row here;
 * the Tracker turns "earned" into "due" from it.
 *
 * Small table, edited in place. Nothing here is evidence — a section keeps
 * its own hours and CCL code — so editing a course is safe at any time; what
 * changes is how the Tracker reads past completions.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { useAuth } from '../auth/useAuth.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import Field from '../components/Field.jsx';
import Select from '../components/Select.jsx';

const KINDS = [
  { value: 'certification', label: 'Certification (expires)' },
  { value: 'continuing_education', label: 'Continuing education (accrues)' },
];

const EMPTY = { code: '', name: '', kind: 'certification', hours: '', recert_hours: '', valid_months: '', notes: '' };

function toPayload(f) {
  const num = (v) => (String(v ?? '').trim() === '' ? null : Number(v));
  return {
    code: f.code.trim(),
    name: f.name.trim(),
    kind: f.kind,
    hours: num(f.hours),
    recert_hours: num(f.recert_hours),
    valid_months: f.kind === 'continuing_education' ? null : num(f.valid_months),
    notes: f.notes.trim() || null,
  };
}

function toForm(c) {
  const str = (v) => (v === null || v === undefined ? '' : String(v));
  return {
    code: str(c.code),
    name: str(c.name),
    kind: c.kind,
    hours: str(c.hours),
    recert_hours: str(c.recert_hours),
    valid_months: str(c.valid_months),
    notes: str(c.notes),
  };
}

function validityLabel(c) {
  if (c.kind === 'continuing_education' || !c.valid_months) return 'Never expires';
  if (c.valid_months % 12 === 0) {
    const y = c.valid_months / 12;
    return `${y} ${y === 1 ? 'year' : 'years'}`;
  }
  return `${c.valid_months} months`;
}

export function CatalogPage() {
  const { profile } = useAuth();
  const [courses, setCourses] = useState(undefined);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    try {
      setCourses(await apiFetch('/api/v1/courses'));
    } catch (err) {
      setError(err);
      setCourses(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  async function save(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (editingId) {
        await apiFetch(`/api/v1/courses/${editingId}`, { method: 'PATCH', body: JSON.stringify(toPayload(form)) });
      } else {
        await apiFetch('/api/v1/courses', {
          method: 'POST',
          body: JSON.stringify({ ...toPayload(form), institution_id: profile?.institution?.id }),
        });
      }
      setForm(EMPTY);
      setAdding(false);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(c) {
    setError(null);
    setBusy(true);
    try {
      await apiFetch(`/api/v1/courses/${c.id}`, { method: 'PATCH', body: JSON.stringify({ is_active: !c.is_active }) });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  function edit(c) {
    setForm(toForm(c));
    setEditingId(c.id);
    setAdding(true);
  }

  const visible = (courses ?? []).filter((c) => showInactive || c.is_active);

  return (
    <>
      <div className="page-head">
        <h1>Catalog</h1>
      </div>
      <ErrorBanner error={error} />
      <p className="muted">
        What completing a section is worth, and for how long. Link a section to one of these on the
        section page; the Tracker then knows when each person’s card runs out. Expiry is counted from
        the day of the class.
      </p>

      {courses === undefined && (
        <p className="muted" aria-busy="true">
          Loading…
        </p>
      )}

      {courses && (
        <section className="panel" aria-labelledby="catalog-heading">
          <h2 id="catalog-heading">Courses</h2>
          {visible.length === 0 ? (
            <p className="muted">No courses yet. Add the first one below.</p>
          ) : (
            <div className="schedule__scroll">
              <table className="schedule__table">
                <thead>
                  <tr>
                    <th scope="col">Code</th>
                    <th scope="col">Name</th>
                    <th scope="col">Hours</th>
                    <th scope="col">Recert</th>
                    <th scope="col">Good for</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((c) => (
                    <tr key={c.id} className={c.is_active ? undefined : 'row--muted'}>
                      <td>
                        <strong>{c.code}</strong>
                        {!c.is_active && <span className="badge badge--archived"> retired</span>}
                      </td>
                      <td>
                        {c.name}
                        {c.notes && (
                          <>
                            <br />
                            <span className="muted">{c.notes}</span>
                          </>
                        )}
                      </td>
                      <td>{c.hours ?? <span className="muted">—</span>}</td>
                      <td>{c.recert_hours ?? <span className="muted">—</span>}</td>
                      <td>{validityLabel(c)}</td>
                      <td className="toolbar">
                        <button type="button" className="btn btn--quiet" onClick={() => edit(c)} disabled={busy}>
                          Edit
                        </button>
                        <button type="button" className="btn btn--quiet" onClick={() => toggleActive(c)} disabled={busy}>
                          {c.is_active ? 'Retire' : 'Restore'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="toolbar">
            {!adding && (
              <button
                type="button"
                className="btn btn--auto"
                onClick={() => {
                  setForm(EMPTY);
                  setEditingId(null);
                  setAdding(true);
                }}
                disabled={busy}
              >
                Add a course
              </button>
            )}
            {(courses ?? []).some((c) => !c.is_active) && (
              <label className="checkbox">
                <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                <span>Show retired</span>
              </label>
            )}
          </p>
        </section>
      )}

      {adding && (
        <form className="panel" onSubmit={save} aria-labelledby="course-form-heading">
          <h2 id="course-form-heading">{editingId ? `Edit ${form.code || 'course'}` : 'New course'}</h2>
          <div className="field-row">
            <Field label="Short code" value={form.code} onChange={(v) => set({ code: v })} hint="PHTLS, ACLS, DAA…" disabled={busy} />
            <Select label="Kind" value={form.kind} onChange={(v) => set({ kind: v })} options={KINDS} disabled={busy} />
          </div>
          <Field label="Name" value={form.name} onChange={(v) => set({ name: v })} disabled={busy} />
          <div className="field-row">
            <Field label="Hours (initial)" type="number" value={form.hours} onChange={(v) => set({ hours: v })} required={false} disabled={busy} />
            <Field label="Hours (recert)" type="number" value={form.recert_hours} onChange={(v) => set({ recert_hours: v })} required={false} disabled={busy} />
            {form.kind === 'certification' && (
              <Field
                label="Good for (months)"
                type="number"
                value={form.valid_months}
                onChange={(v) => set({ valid_months: v })}
                hint="48 = 4 years. Blank = never expires."
                required={false}
                disabled={busy}
              />
            )}
          </div>
          <Field label="Notes" value={form.notes} onChange={(v) => set({ notes: v })} required={false} disabled={busy} />
          <p className="toolbar">
            <button type="submit" className="btn btn--primary btn--auto" disabled={busy || !form.code.trim() || !form.name.trim()}>
              {busy ? 'Saving…' : editingId ? 'Save changes' : 'Add course'}
            </button>
            <button
              type="button"
              className="btn btn--quiet btn--auto"
              onClick={() => {
                setAdding(false);
                setEditingId(null);
                setForm(EMPTY);
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </p>
        </form>
      )}
    </>
  );
}

export default CatalogPage;
